import Vue from 'vue';
import Vuex from 'vuex';
import register from './modules/register';
import passwordReset from './modules/passwordReset';
import loyaltyPanel from './modules/loyaltyPanel';
import storePanel from './modules/storePanel';
import supplierPanel from './modules/supplierPanel';
import general from './modules/general';
import exportLinks from './modules/exportLinks';

import API from '@/models/API';
import router from '@/routes';
import { isEmpty } from 'lodash';

Vue.use(Vuex);

import axios from 'axios';
import translationData from '@/utils/translations/global';
const translations = translationData.data().translations;
axios.defaults.headers.Authorization = `Bearer ${localStorage.getItem('accessToken')}`;

API.interceptors.request.use((request) => {
  let [baseUrl, queryString] = request.url.split('?');
  if (queryString) {
    let params = queryString
      .split('&')
      .map((param) => {
        const [key, value] = param.split('=');
        return value ? `${key}=${encodeURIComponent(value)}` : param;
      })
      .join('&');

    request.url = `${baseUrl}?${params}`;
  }
  return request;
});

API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.data?.code === 401) {
      let errorMessage = error.response?.data?.message;
      if (errorMessage != 'Invalid login credentials.') {
        errorMessage = translations.sessionExpired?.[router?.currentRoute?.params?.lang || 'en'];
        store?.commit('setNotification', {
          show: true,
          type: 'error',
          text: errorMessage,
        });
      }
      store?.dispatch('logout');
    }
    throw error;
  }
);
const getDefaultState = () => ({
  accessToken: '',
  storeId: '',
  store: {},
  user: {},
  userStoresAll: [],
  userStores: [],
  userStoresTotalPages: null,
  permissions: {},
  storePermissions: {},
  dialog: false,
  deleteDialog: false,
  permissionDialog: false,
  loading: false,
  errorMessage: '',
  resetSuccess: false,
  resetValidation: false,
  serverItemsLength: 0,
  serverItemsPerPage: 12,
  notification: {
    show: false,
    type: '',
    text: '',
  },
  activeCountries: [],
  systemNotifications: [],
  systemNotificationsEnabled: [],
  notificationCategories: [],
  appProvider: {},
  language: 'en',
  superUser: false,
  isSuperAdmin: false,
  exportCSVStatus: false,
  notificationsTrigger: false,
  supplierInfo: {},
  get panelType() {
    return ((router?.history.current.path || '').split('/') || [])[2] || 'storePanel';
  },
  serverPagination: {},
});

const store = new Vuex.Store({
  modules: {
    register,
    passwordReset,
    loyaltyPanel,
    storePanel,
    supplierPanel,
    general,
    exportLinks,
  },

  state: getDefaultState(),

  getters: {
    activeCountries: (state) => state.activeCountries,
    systemNotifications: (state) => state.systemNotifications,
    appProvider: (state) => state.appProvider,
    systemNotificationsEnabled: (state) => state.systemNotificationsEnabled,
    notificationCategories: (state) => state.notificationCategories,
    getPermission: (state) => (path) => {
      try {
        if (!path || state.superUser) return true;
        const hasPermission = eval(`state.permissions?.${path}`) || false;
        const hasStorePermission = eval(`state.storePermissions?.${path}`) || false;
        return hasPermission && hasStorePermission;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    getProcessedModeTranslation: (state) => (text) => {
      const mode = state.store?.display_mode || 'points';
      if (!text || typeof text !== 'string') return text;

      const stringsToReplace = {
        points: mode,
        'loyalty points': mode,
      };

      let modeText = text;
      Object.keys(stringsToReplace).forEach((key) => {
        const regex = new RegExp(`\\b${key}\\b`, 'gi');
        modeText = modeText.replace(regex, stringsToReplace[key]);
      });

      return modeText;
    },
  },

  mutations: {
    resetState(state) {
      Object.assign(state, getDefaultState());
    },

    setAccessToken(state, payload) {
      state.accessToken = payload;
      API.defaults.headers.Authorization = `Bearer ${payload}`;
    },

    setStoreId(state, payload) {
      state.storeId = payload;
    },

    setStore(state, payload) {
      state.store = payload;
    },

    setUser(state, payload) {
      state.user = payload;
    },

    setUserStores(state, payload) {
      state.userStores = payload;
    },
    setUserStoresAll(state, payload) {
      state.userStoresAll = payload;
    },

    setPermissions(state, payload) {
      state.permissions = payload;
    },

    setStorePermissions(state, payload) {
      state.storePermissions = payload;
    },

    setDialog(state, payload) {
      state.dialog = payload;
    },

    setDeleteDialog(state, payload) {
      state.deleteDialog = payload;
    },

    setPermissionDialog(state, payload) {
      state.permissionDialog = payload;
    },

    setLoading(state, payload) {
      state.loading = payload;
    },

    setErrorMessage(state, payload) {
      state.errorMessage = payload;
    },

    setResetSuccess(state, payload) {
      state.resetSuccess = payload;
    },

    setResetValidation(state, payload) {
      state.resetValidation = payload;
    },

    setServerItemsLength(state, payload) {
      state.serverItemsLength = payload;
    },

    setServerPagination(state, payload) {
      state.serverPagination = payload;
    },

    setServerItemsPerPage(state, payload) {
      state.serverItemsPerPage = payload;
    },

    setNotification(state, payload) {
      state.notification = payload;
    },

    setActiveCountries(state, payload) {
      state.activeCountries = payload;
    },

    setSystemNotifications(state, payload) {
      state.systemNotifications = payload;
    },

    setSystemNotificationsEnabled(state, payload) {
      state.systemNotificationsEnabled = payload;
    },

    setNotificationCategories(state, payload) {
      state.notificationCategories = payload;
    },

    setAppProvider(state, payload) {
      state.appProvider = payload;
    },

    setLanguage(state, payload) {
      state.language = payload;
    },

    setSuperUser(state, payload) {
      state.superUser = payload;
    },

    setIsSuperAdmin(state, payload) {
      state.isSuperAdmin = payload;
    },

    setUserStoresTotalPages(state, payload) {
      state.userStoresTotalPages = payload;
    },

    setExportCSVStatus(state, payload) {
      state.exportCSVStatus = payload;
    },

    setNotificationsTrigger(state, payload) {
      state.notificationsTrigger = payload;
    },

    setSupplierInfo(state, payload) {
      state.supplierInfo = payload;
    },
  },

  actions: {
    resetAllModuleStates({ commit }) {
      Object.keys(this._modulesNamespaceMap).forEach((ns) => {
        commit(`${ns}resetState`, null, { root: true });
      });
    },

    async getUser({ commit, dispatch, state }, store_id = localStorage.getItem('storeId')) {
      try {
        if (!store_id) {
          const { data } = await API.get(`/users/me`);
          commit('setUser', data.data.user);
          return;
        }
        if (state.store?.store_id != store_id) {
          dispatch('resetAllModuleStates');
        }
        const { data } = await API.get(
          `/users/me?store-id=${store_id || localStorage.getItem('storeId')}`
        );
        const { user_store_permissions, store_permissions, user } = data.data;
        commit('setUser', user);

        commit('setIsSuperAdmin', user.is_super_admin); //the one god user

        // users that have every permission on the visiting panel
        commit('setSuperUser', !!user_store_permissions['*']);

        const langMap = { 1: 'en', 2: 'el', 3: 'it' };
        commit('setLanguage', langMap[user.language_id] || 'en');

        commit('setPermissions', user_store_permissions);
        commit('setStorePermissions', store_permissions);
      } catch (ex) {
        console.error(ex);
        if (ex.response) {
          console.error(ex.response);
          if (ex.response?.statusText.toLowerCase().includes('unauthorized')) {
            dispatch('logout');
          }
        }
      }
    },

    async logout({ commit, dispatch }) {
      console.log('logout');
      commit('setUser', {});
      commit('setPermissions', {});
      commit('setSuperUser', false);
      commit('setIsSuperAdmin', false);
      localStorage.removeItem('storeId');
      commit('setStore', {});
      commit('setStorePermissions', {});
      commit('setStoreId', '');
      localStorage.removeItem('companyId');
      commit('setSupplierInfo', {});
      commit('setUserStores', []);
      commit('setUserStoresTotalPages', null);
      commit('setDialog', false);
      commit('setDeleteDialog', false);
      commit('setPermissionDialog', false);
      localStorage.removeItem('accessToken');
      commit('setAccessToken', '');
      router.replace({
        name: 'SignIn',
        params: {
          lang: router.currentRoute.params.lang || 'en',
        },
      });
      dispatch('resetAllModuleStates');
      commit('resetState');
    },

    async changeLanguage({ commit }, body) {
      try {
        await await API.put(`/users/me/change-user-language`, body);

        if (body.language_id == 1) {
          commit('setLanguage', 'en');
        } else if (body.language_id == 2) {
          commit('setLanguage', 'el');
        } else if (body.language_id == 3) {
          commit('setLanguage', 'it');
        }
      } catch (ex) {
        console.error(ex);
      }
    },

    async updateProfileName({ commit, state }, body) {
      try {
        commit('setLoading', true);
        let res = await await API.patch(`/users/me/profile`, body);
        if (res.status == 200) {
          state.user.full_name = res.data.data.full_name;
        } else {
          commit('setNotification', {
            show: true,
            type: 'error',
            text: res.data.message,
          });
        }
        commit('setLoading', false);

        return res;
      } catch (ex) {
        commit('setLoading', false);
        commit('setNotification', {
          show: true,
          type: 'error',
          text: ex?.data?.message || 'Error',
        });
        console.error(ex);
      }
    },

    async getUserStores({ commit, state, dispatch }, body = {}) {
      try {
        commit('setLoading', true);
        body.query = body?.query || '';
        axios.defaults.headers.Authorization = `Bearer ${state.accessToken}`;
        const { data } = await API.get(`/users/stores/all${body.query}`);
        commit('setUserStores', data.data.stores);
        commit('setLoading', false);
      } catch (ex) {
        console.error(ex);
        // if(ex.response&&ex.response.statusText == 'Unauthorized'){
        dispatch('logout');
        commit('setLoading', false);

        // }
      }
    },

    async getSupplierInfo({ commit, state }) {
      try {
        commit('setLoading', true);
        axios.defaults.headers.Authorization = `Bearer ${state.accessToken}`;
        const { data } = await API.get(`/suppliers/${localStorage.getItem('storeId')}/me`);

        commit('setSupplierInfo', data.data);
        commit('setLoading', false);
      } catch (ex) {
        console.error(ex.response);
        commit('setLoading', false);
      }
    },

    async loadActiveCountries({ commit }) {
      try {
        const { data } = await API.get(`/common/countries`);
        commit('setActiveCountries', data.data);
      } catch (ex) {
        console.error(ex);
      }
    },

    async loadSystemNotifications({ commit }) {
      try {
        const { data } = await API.get(`/common/system-notifications`);
        data.data.forEach((e) => {
          switch (e.type) {
            case 'mail':
              e.displayName = 'Email';
              break;
            case 'push':
              e.displayName = 'Push Notification';
              break;
            case 'sms':
              e.displayName = 'SMS/Viber';
              break;
            default:
              e.displayName = e.name;
              break;
          }
        });
        commit('setSystemNotifications', data.data);
      } catch (ex) {
        console.error(ex);
      }
    },

    async loadSystemNotificationsEnabled({ commit }) {
      try {
        const { data } = await API.get(`/common/enabled-system-notifications`);
        data.data.forEach((e) => {
          switch (e.type) {
            case 'mail':
              e.displayName = 'Email';
              break;
            case 'push':
              e.displayName = 'Push Notification';
              break;
            case 'sms':
              e.displayName = 'SMS/Viber';
              break;
            default:
              e.displayName = e.name;
              break;
          }
        });
        commit('setSystemNotificationsEnabled', data.data);
      } catch (ex) {
        console.error(ex);
      }
    },

    async loadNotificationCategories({ commit }) {
      try {
        const { data } = await API.get(`/common/notification-categories`);
        commit('setNotificationCategories', data.data);
      } catch (ex) {
        console.error(ex);
      }
    },

    async loadAppProvider({ commit }) {
      try {
        const { data } = await API.get(`/common/app-providers`);
        commit(
          'setAppProvider',
          data.data.find((ap) => (ap.store_id = this.storeId))
        );
      } catch (ex) {
        console.error(ex);
      }
    },

    async getCouponDetails({ commit }, id) {
      try {
        const { data } = await API.get(`/common/fb/coupons/${id}`);
      } catch (ex) {
        console.error(ex.response.data.message);
      }
    },

    async updateExportNotification({ commit }, data) {
      try {
        if (data.status == 'SUCCESS') {
          commit('setExportCSVStatus', true);

          commit('setNotification', {
            show: true,
            type: 'success',
            text: data.message,
          });
        } else {
          commit('setNotification', {
            show: true,
            type: 'error',
            text: data.message,
          });
        }
      } catch (ex) {
        console.error(ex.response.data.message);
      }
    },

    async newNotification({ commit, state }, data) {
      try {
        if (data.status == 'SUCCESS') {
          commit('setNotification', {
            show: true,
            type: data.type || 'success',
            text: data.message,
            persistent: data.persistent,
            url: data.url,
            urlText: data.urlText,
          });
          commit('setNotificationsTrigger', !state.notificationsTrigger);
        } else {
          commit('setNotification', {
            show: true,
            type: 'error',
            text: data.message,
            persistent: data.persistent,
            url: data.url,
            urlText: data.urlText,
          });
        }
      } catch (ex) {
        console.error(ex.response.data.message);
      }
    },

    async getPermission({ commit, dispatch, state }, path) {
      try {
        if (!path) {
          await dispatch('getUser');
        }
        return (
          state.superUser ||
          (eval('state.permissions?.' + path) && eval('state.storePermissions?.' + path))
        );
      } catch (error) {
        return undefined;
      }
    },

    async checkVatNumber({ commit, dispatch, rootState }, { afm, country_id }) {
      try {
        if (!afm || !country_id) {
          return false;
        }
        let res = await API.post(`/common/check-vat-number`, { afm, country_id });
        return res;
      } catch (ex) {
        return ex.response;
      }
    },
  },
});

export default store;
